import { DragDropModule } from '@angular/cdk/drag-drop';
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { NgModule, Type } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PageHeaderModule } from '@delon/abc/page-header';
import { SEModule } from '@delon/abc/se';
import { STModule } from '@delon/abc/st';
import { XlsxModule } from '@delon/abc/xlsx';
import { CurrencyPipeModule } from '@delon/util/pipes/currency';
import {
  CopyTransactionComponent
} from '@shared-feature/components/actions/doc-edit-actions/copy-transaction/copy-transaction.component';
import { EmailStatusModalComponent } from '@shared-feature/components/email-status-modal/email-status-modal.component';
import { PriSelectComponent } from '@shared-feature/components/select/pri-select/pri-select.component';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzResizableModule } from 'ng-zorro-antd/resizable';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzTreeSelectModule } from 'ng-zorro-antd/tree-select';
import { QuillModule } from 'ngx-quill';

import { OptionalStylesService } from '../core/optional-styles.service';
import { EinvoiceStatusComponent } from '../components/einvoice/einv-status/einv-status.component';
import { EInvoiceBillingFreqSelectComponent } from '../components/einvoice/einvoice-billing-freq-select/einvoice-billing-freq-select.component';
import { EInvoiceStockClassificationSelectComponent } from '../components/einvoice/einvoice-stock-classification-select/einvoice-stock-classification-select.component';
import { EInvoiceTinTypeSelectComponent } from '../components/einvoice/einvoice-tintype-select/einvoice-tintype-select.component';
import { EInvoiceScanTaxPayerQRComponent } from '../components/einvoice/scan-taxpayer-qr/scan-taxpayer-qr.component';
import { EInvoiceUuidModalComponent } from '../components/einvoice/uuid-modal/uuid-modal.component';
import { ListViewSwitcherComponent } from '../components/list-view-switcher/list-view-switcher.component';
import { AccountCodeComponent } from '../components/modal/account-code/account-code-modal.component';
import { StockItemEInvoiceRequiredModalComponent } from '../components/modal/stock-item-einvoice-required-modal/stock-item-einvoice-required-modal.component';
import { PeppolStatusComponent } from '../components/peppol/peppol-status/peppol-status.component';
import { CurrencySelectComponent } from '../components/select/currency-select/currency-select.component';
import { EInvoiceMYStateSelectComponent } from '../components/select/einvoice-my-state-select/einvoice-my-state-select.component';
import { EInvoiceOriginCountrySelectComponent } from '../components/select/einvoice-origin-country-select/einvoice-origin-country-select.component';
import { TariffTaxCodeSelectComponent } from '../components/select/tariff-tax-code-select/tariff-tax-code-select.component';
import { InputModule } from '../input/input.module';
import { ReceivePaymentService } from '../routes/ar/receive-payment/aror.service';
import { SharedModule } from '../shared/shared.module';
import { MinusSignToParensPipe } from './_providers/minus-sign-to-parens.pipe';
import { PurchaseDocTransferService } from './_providers/purchase-doc-transfer.service';
import { SalesDocTransferService } from './_providers/sales-doc-transfer.service';
import { BatchEmailComponent } from './components/actions/batch-email/batch-email.component';
import { BatchPrintComponent } from './components/actions/batch-print/batch-print.component';
import { DataEditActionsComponent } from './components/actions/data-edit-actions/data-edit-actions.component';
import { TransactionSenderComponent } from './components/actions/doc-edit-actions/components/transaction-sender.component';
import { DocEditActionsComponent } from './components/actions/doc-edit-actions/doc-edit-actions.component';
import { DocListActionsComponent } from './components/actions/doc-list-actions/doc-list-actions.component';
import { PageHowToToolbarButtonComponent } from './components/page-how-to/page-how-to-toolbar-button.component';
import { ReportPreviewDocComponent } from './components/actions/preview/preview-doc.component';
import { APWtaxKnockoffComponent } from './components/ap-knockoff/wtax-knockoff/wtax-knockoff.component';
import { ARWtaxKnockoffComponent } from './components/ar-knockoff/wtax-knockoff/wtax-knockoff.component';
import { ARWVatKnockoffComponent } from './components/ar-knockoff/wvat-knockoff/wvat-knockoff.component';
import { AutoCodeComponent } from './components/auto-code/auto-code.component';
import { BankChargesComponent } from './components/bank-charges/bank-charges.component';
import { DepositComponent } from './components/deposit/deposit.component';
import { DevExpressModule } from './components/dev-express/dev-express.module';
import { EditItemComponent } from './components/edit-item/edit-item.component';
import { FurtherDescriptionComponent } from './components/f-desc/f-desc.component';
import { FilterFieldComponent } from './components/filter-list/filter-field/filter-field.component';
import { FilterListComponent } from './components/filter-list/filter-list.component';
import { GLPostingModalComponent } from './components/gl-posting-modal/gl-posting-modal.component';
import { FieldMapperModalComponent } from './components/import-excel-base/field-mapper-modal/field-mapper-modal.component';
import { ImportExcelDrawerComponent } from './components/import-excel-drawer/import-excel-drawer.component';
import { ImportExcelPageHeaderComponent } from './components/import-excel-page-header/import-excel-page-header.component';
import { ImportExcelComponent } from './components/import-excel/import-excel.component';
import { ApCateAddConfigService } from './components/inline-add-panel/ap-cate-add-config.service';
import { InlineAddPanelComponent } from './components/inline-add-panel/inline-add-panel.component';
import { PurchaserAddConfigService } from './components/inline-add-panel/purchaser-add-config.service';
import { StockCategoryAddConfigService } from './components/inline-add-panel/stock-category-add-config.service';
import { StockClassAddConfigService } from './components/inline-add-panel/stock-class-add-config.service';
import { StockGroupAddConfigService } from './components/inline-add-panel/stock-group-add-config.service';
import { StockLocationAddConfigService } from './components/inline-add-panel/stock-location-add-config.service';
import { InventoryTranxSummaryComponent } from './components/inventory-tranx-summary/inventory-tranx-summary.component';
import { JvTranxSummaryComponent } from './components/jv-tranx-summary/jv-tranx-summary.component';
import { DocSummaryComponent } from './components/labels/doc-summary/doc-summary.component';
import { BatchEmailModalComponent } from './components/modal/batch-email-modal/batch-email-modal.component';
import { BirEditModalComponent } from './components/modal/bir-edit-modal/bir-edit-modal.component';
import { BirExistModalComponent } from './components/modal/bir-exist-modal/bir-exist-modal.component';
import { BirInfoViewComponent } from './components/modal/bir-exist-modal/bir-info-view.component';
import { BirReplaceModalComponent } from './components/modal/bir-replace-modal/bir-replace-modal.component';
import { CustomerInfoModalComponent } from './components/modal/customer-info-modal/customer-info-modal.component';
import { EinvoiceConfirmationModalComponent } from './components/modal/einvoice-confirmation-modal/einvoice-confirmation-modal.component';
import { GlobalCreditLimitModalComponent } from './components/modal/global-credit-limit-modal/global-credit-limit-modal.component';
import { InvoiceEmailModalComponent } from './components/modal/invoice-email-modal/invoice-email-modal.component';
import { MoreDatePickerComponent } from './components/modal/more-date-picker-modal/more-date-picker-modal.component';
import { DuitnowProxyComponent } from './components/modal/online-pay-modal/components/duitnow-proxy/duitnow-proxy.component';
import { DuitnowComponent } from './components/modal/online-pay-modal/components/duitnow/duitnow.component';
import { LocalUobComponent } from './components/modal/online-pay-modal/components/local-uob/local-uob.component';
import { OnlinePaymentViewComponent } from './components/modal/online-pay-modal/components/online-payment-view/online-payment-view.component';
import { UobTtComponent } from './components/modal/online-pay-modal/components/tt/tt.component';
import { OnlinePayModalComponent } from './components/modal/online-pay-modal/online-pay-modal.component';
import { OnlinePaymentService } from './components/modal/online-pay-modal/online-payment.service';
import { PeppolConfirmationModalComponent } from './components/modal/peppol-confirmation-modal/peppol-confirmation-modal.component';
import { RecurringTransactionModalComponent } from './components/modal/recurring-transaction-modal/recurring-transaction-modal.component';
import { SSTReturnDeclarationInfoModalComponent } from './components/modal/sst-return-declaration-info-modal/sst-return-declaration-info-modal.component';
import { SstReturnDeemedPaymentModalComponent } from './components/modal/sst-return-modal/deemed-payment-modal/sst-return-deemed-payment-modal.component';
import { SstReturnDeemedPaymentRecoveryModalComponent } from './components/modal/sst-return-modal/deemed-payment-recovery-modal/sst-return-deemed-payment-recovery-modal.component';
import { StockQuantityControlModalComponent } from './components/modal/stock-quantity-control-modal/sqc-modal.component';
import { SupplierInfoModalComponent } from './components/modal/supplier-info-modal/supplier-info-modal.component';
import { TaxDatePickerComponent } from './components/modal/tax-date-picker-modal/tax-date-picker-modal.component';
import { ToolguideModalComponent } from './components/modal/toolguide-modal/toolguide-modal.component';
import { PurchaseDocTransferComponent } from './components/purchase-doc-transfer/purchase-doc-transfer.component';
import { ReliefImportationModalComponent } from './components/relief-importation-modal/relief-importation-modal.component';
import { SalesDocTransferComponent } from './components/sales-doc-transfer/sales-doc-transfer.component';
import { AreaSelectItemComponent } from './components/select/_templates/area-item/area-item.component';
import { BirItemSelectItemComponent } from './components/select/_templates/bir-item/bir-item.component';
import { BirSupplierItemSelectItemComponent } from './components/select/_templates/bir-item/supplier/bir-supplier-item.component';
import { CustomerSelectItemComponent } from './components/select/_templates/customer-item/customer-item.component';
import { DocItemComponent } from './components/select/_templates/doc-item/doc-item.component';
import { SupplierSelectItemComponent } from './components/select/_templates/supplier-item/supplier-item.component';
import { BankAccountSelectComponent } from './components/select/account/bac-select/bac-select.component';
import { BankCashAccountSelectComponent } from './components/select/account/cac-select/cac-select.component';
import { SupplierControlAccountSelectComponent } from './components/select/account/ccac-account-select/ccac-select.component';
import { DepreciationAssetAccountSelectComponent } from './components/select/account/da-account-select/da-account-select.component';
import { CustomerControlAccountSelectComponent } from './components/select/account/dac-account-select/dac-select.component';
import { ExpenseAccountSelectComponent } from './components/select/account/exp-select/exp-select.component';
import { FixedAssetAccountSelectComponent } from './components/select/account/fa-account-select/fa-account-select.component';
import { JvAccountSelectComponent } from './components/select/account/jv-acc-select/jv-acc-select.component';
import { AgentSelectComponent } from './components/select/agent-select/agent-select.component';
import { SupplierCategorySelectComponent } from './components/select/ap-cate-select/ap-cate-select.component';
import { APCNSelectComponent } from './components/select/apcn-select/apcn-select.component';
import { APDNSelectComponent } from './components/select/apdn-select/apdn-select.component';
import { APRefundSelectComponent } from './components/select/aprefund-select/aprefund-select.component';
import { CustomerCategorySelectComponent } from './components/select/ar-cate-select/ar-cate-select.component';
import { ARCNSelectComponent } from './components/select/arcn-select/arcn-select.component';
import { ARDNSelectComponent } from './components/select/ardn-select/ardn-select.component';
import { AreaSelectComponent } from './components/select/area-select/area-select.component';
import { ARRefundSelectComponent } from './components/select/arrefund-select/arrefund-select.component';
import { AssetTypeSelectComponent } from './components/select/asset-type-select/asset-type-select.component';
import { AutoReconMapItemSelectComponent } from './components/select/auto-recon-map-item-select/auto-recon-map-item-select.component';
import { QneTplSelectComponent } from './components/select/auto-recon-qne-tpl-select/auto-recon-qne-tpl-select.component';
import { BillSelectComponent } from './components/select/bills-select/bills-select.component';
import { BirClassSelectComponent } from './components/select/bir-class-select/bir-class-select.component';
import { BirSupplierClassSelectComponent } from './components/select/bir-class-select/supplier/bir-supplier-class-select.component';
import { CpSelectComponent } from './components/select/cp-select/cp-select.component';
import { CpRefundSelectComponent } from './components/select/cpr-select/cpr-select.component';
import { CreditableSelectComponent } from './components/select/creditable-select/creditable-select.component';
import { CsSelectComponent } from './components/select/cs-select/cs-select.component';
import { CsRefundSelectComponent } from './components/select/csr-select/csr-select.component';
import { CustomerBirSelectComponent } from './components/select/cust-bir-select/cust-bir-select.component';
import { CustomerStatusSelectComponent } from './components/select/cust-status-select/cust-status-select.component';
import { CustomerSelectComponent } from './components/select/customer-select/customer-select.component';
import { DOAddressSelectComponent } from './components/select/do-address-select/do-address-select.component';
import { DoSelectComponent } from './components/select/do-select/do-select.component';
import { EInvoiceApTinTypeSelectComponent } from './components/select/einvoice-ap-tintype-select/einvoice-ap-tintype-select.component';
import { EInvoiceCountriesSelectComponent } from './components/select/einvoice-countries-select/einvoice-countries-select.component';
import { EInvoiceMsicCodeSelectComponent } from './components/select/einvoice-msic-code-select/einvoice-msic-code-select.component';
import { EInvoicePaymSelectComponent } from './components/select/einvoice-payment-method-select/einvoice-payment-method-select.component';
import { EInvoiceTranxTypeSelectComponent } from './components/select/einvoice-tranx-type-select/einvoice-tranx-type-select.component';
import { EInvoiceTypeSelectComponent } from './components/select/einvoice-type-select/einvoice-type-select.component';
import { EInvoiceUomCodeSelectComponent } from './components/select/einvoice-uom-code-select/einvoice-uom-code-select.component';
import { EInvoiceValidationControlSelectComponent } from './components/select/einvoice-validation-control-select/einvoice-validation-control-select.component';
import { FASelectComponent } from './components/select/fa-select/fa-select.component';
import { GenderSelectComponent } from './components/select/gender-select/gender-select.component';
import { GLAccountSelectComponent } from './components/select/gl-account-select/gl-account-select.component';
import { GrnSelectComponent } from './components/select/grn-select/grn-select.component';
import { InvoiceSelectComponent } from './components/select/invoice-select/invoice-select.component';
import { JournalSelectComponent } from './components/select/journal-select/journal-select.component';
import { JvBirSelectComponent } from './components/select/jv-bir-select/jv-bir-select.component';
import { MultiMSICSelectComponent } from './components/select/multi-msic-select/multi-msic-select.component';
import { PaymentSelectComponent } from './components/select/payment-select/payment-select.component';
import { PcnSelectComponent } from './components/select/pcn-select/pcn-select.component';
import { PdnSelectComponent } from './components/select/pdn-select/pdn-select.component';
import { PinvSelectComponent } from './components/select/pinv-select/pinv-select.component';
import { PoSelectComponent } from './components/select/po-select/po-select.component';
import { PrSelectComponent } from './components/select/pr-select/pr-select.component';
import { ProjectOBSelectComponent } from './components/select/project-ob-select/project-ob-select.component';
import { SimpleProjectSelectComponent } from './components/select/project-select-simple/project-select.component';
import { ProjectSelectComponent } from './components/select/project-select/project-select.component';
import { ProjectTreeSelectComponent } from './components/select/project-tree-select/project-tree-select.component';
import { PurchaserSelectComponent } from './components/select/purchaser-select/purchaser-select.component';
import { PvBirSelectComponent } from './components/select/pv-bir-select/pv-bir-select.component';
import { QtSelectComponent } from './components/select/qt-select/qt-select.component';
import { RDOSelectComponent } from './components/select/rdo-select/rdo-select.component';
import { ReceiptSelectComponent } from './components/select/receipt-select/receipt-select.component';
import { RvBirSelectComponent } from './components/select/rv-bir-select/rv-bir-select.component';
import { SCNSelectComponent } from './components/select/scn-select/scn-select.component';
import { SDNSelectComponent } from './components/select/sdn-select/sdn-select.component';
import { SimpleMultiSelectComponent } from './components/select/simple-select/simple-select.component';
import { SINVSelectComponent } from './components/select/sinv-select/sinv-select.component';
import { SoSelectComponent } from './components/select/so-select/so-select.component';
import { StockAccountPresetsSelectComponent } from './components/select/stock-account-presets-select/stock-account-presets-select.component';
import { StockAdjustmentSelectComponent } from './components/select/stock-adj-select/stock-adj-select.component';
import { StockCategorySelectComponent } from './components/select/stock-category-select/stock-category-select.component';
import { StockClassSelectComponent } from './components/select/stock-class-select/stock-class-select.component';
import { StockGroupSelectComponent } from './components/select/stock-group-select/stock-group-select.component';
import { StockInSelectComponent } from './components/select/stock-in-select/stock-in-select.component';
import { SimpleStockLocationSelectComponent } from './components/select/stock-location-select-simple/stock-location-select.component';
import { StockLocationSelectComponent } from './components/select/stock-location-select/stock-location-select.component';
import { StockOpeningBalanceSelectComponent } from './components/select/stock-ob-select/stock-ob-select.component';
import { StockOutSelectComponent } from './components/select/stock-out-select/stock-out-select.component';
import { StockSelectComponent } from './components/select/stock-select/stock-select.component';
import { StockTransferSelectComponent } from './components/select/stock-transfer-select/stock-transfer-select.component';
import { SupplierBirSelectComponent } from './components/select/supplier-bir-select/supplier-bir-select.component';
import { SupDOAddressSelectComponent } from './components/select/supplier-do-address-select/supplier-do-address-select.component';
import { SupplierSelectComponent } from './components/select/supplier-select/supplier-select.component';
import { SupplierStatusSelectComponent } from './components/select/supplier-status-select/supplier-status-select.component';
import { InputTaxCodeSelectComponent } from './components/select/taxcode/input-tax-code-select/input-tax-code-select.component';
import { OutputTaxCodeSelectComponent } from './components/select/taxcode/output-tax-code-select/output-tax-code-select.component';
import { TaxCodeMultiSelectComponent } from './components/select/taxcode/tax-code-multi-select/tax-code-multi-select.component';
import { TaxCodeSelectComponent } from './components/select/taxcode/tax-code-select/tax-code-select.component';
import { TaxExemptionRateSelectComponent } from './components/select/taxcode/taxcode-exemptionrate-select/taxcode-exemptionrate-select.component';
import { GovTaxCodeMYSelectComponent } from './components/select/taxcode/taxcode-govtc-my-select/taxcode-govtc-my-select.component';
import { GovTaxCodePHSelectComponent } from './components/select/taxcode/taxcode-govtc-ph-select/taxcode-govtc-ph-select.component';
import { TaxTypeMYSelectComponent } from './components/select/taxcode/taxcode-taxtype-my-select/taxcode-taxtype-my-select.component';
import { TaxTypePHSelectComponent } from './components/select/taxcode/taxcode-taxtype-ph-select/taxcode-taxtype-ph-select.component';
import { TermSelectComponent } from './components/select/term-select/term-select.component';
import { UOMSelectComponent } from './components/select/uom-select/uom-select.component';
import { ValidPostingPeriodRuleSelectComponent } from './components/select/vpp-rule-select/vpp-rule-select.component';
import { InputWTaxCodeSelectComponent } from './components/select/wtaxcode/input-wtax-code-select/input-wtax-code-select.component';
import { OutputWTaxCodeSelectComponent } from './components/select/wtaxcode/output-wtax-code-select/output-wtax-code-select.component';
import { WTaxCodeSelectComponent } from './components/select/wtaxcode/wtax-code-select/wtax-code-select.component';
import { StatusMultiSelectComponent } from './components/status-multi-select/status-multi-select.component';
import { StkPostingModalComponent } from './components/stk-posting-modal/stk-posting-modal.component';
import { TranxSummaryComponent } from './components/tranx-summary/tranx-summary.component';
import { AutoCodeService } from './services/auto-code.service';
import { EinvValidatorsService } from './services/einv-validators.service';
import { TransactionDetailColumnAdjustService } from './services/transaction-detail-column-adjust.service';
import { TranscationDetailKeyKownService } from './services/transcation-detail-key-kown.service';

const STANDALONE_COMPONENTS: Array<Type<void>> = [
  ImportExcelComponent,
  ImportExcelDrawerComponent,
  ImportExcelPageHeaderComponent,
  FieldMapperModalComponent,
  EInvoiceMYStateSelectComponent,
  AccountCodeComponent,
  TariffTaxCodeSelectComponent,
  EInvoicePaymSelectComponent,
  EInvoiceOriginCountrySelectComponent,
  EInvoiceUuidModalComponent,
  TransactionSenderComponent,
  EInvoiceTinTypeSelectComponent,
  EInvoiceApTinTypeSelectComponent,
  EInvoiceStockClassificationSelectComponent,
  EInvoiceBillingFreqSelectComponent,
  EinvoiceStatusComponent,
  PeppolStatusComponent,
  CurrencySelectComponent,
  StockItemEInvoiceRequiredModalComponent,
  SstReturnDeemedPaymentModalComponent,
  SstReturnDeemedPaymentRecoveryModalComponent,
  EInvoiceTypeSelectComponent,
  EInvoiceTranxTypeSelectComponent,
  EInvoiceMsicCodeSelectComponent,
  GLAccountSelectComponent,
  ListViewSwitcherComponent,
  EInvoiceScanTaxPayerQRComponent,
  BankCashAccountSelectComponent
];

// #region third libs

const THIRDMODULES: Array<Type<void>> = [
  CurrencyPipeModule,
  STModule,
  SEModule,
  PageHeaderModule,
  NzTableModule,
  NzResizableModule,
  NzToolTipModule,
  NzAutocompleteModule,
  NzDatePickerModule,
  NzDropDownModule,
  NzPopoverModule,
  NzPopconfirmModule,
  NzSelectModule,
  NzDrawerModule,
  NzRadioModule,
  NzDescriptionsModule,
  NzDividerModule,
  NzTagModule,
  XlsxModule,
  QuillModule,
  NzTabsModule,
  NzSpaceModule,
  NzTreeSelectModule,
  DragDropModule,
  OverlayModule,
  NzModalModule,
  DevExpressModule
];

// #endregion

// #region your componets & directives

const COMPONENTS: Array<Type<void>> = [
  AreaSelectItemComponent,
  CreditableSelectComponent,
  BirItemSelectItemComponent,
  CustomerBirSelectComponent,
  SupplierBirSelectComponent,
  RvBirSelectComponent,
  PvBirSelectComponent,
  JvBirSelectComponent,
  BirExistModalComponent,
  BirInfoViewComponent,
  BirEditModalComponent,
  BirReplaceModalComponent,
  OnlinePayModalComponent,
  OnlinePaymentViewComponent,
  DuitnowComponent,
  DuitnowProxyComponent,
  UobTtComponent,
  LocalUobComponent,
  EditItemComponent,
  DocSummaryComponent,
  AreaSelectComponent,
  AgentSelectComponent,
  CustomerSelectComponent,
  SupplierSelectComponent,
  TermSelectComponent,
  JvAccountSelectComponent,
  FixedAssetAccountSelectComponent,
  DepreciationAssetAccountSelectComponent,
  BankAccountSelectComponent,
  ExpenseAccountSelectComponent,
  ReceiptSelectComponent,
  PaymentSelectComponent,
  JournalSelectComponent,
  CustomerControlAccountSelectComponent,
  SupplierControlAccountSelectComponent,
  CustomerCategorySelectComponent,
  SupplierCategorySelectComponent,
  StockSelectComponent,
  UOMSelectComponent,
  TaxCodeSelectComponent,
  InputTaxCodeSelectComponent,
  OutputTaxCodeSelectComponent,
  WTaxCodeSelectComponent,
  InputWTaxCodeSelectComponent,
  OutputWTaxCodeSelectComponent,
  PurchaserSelectComponent,
  BillSelectComponent,
  APDNSelectComponent,
  APCNSelectComponent,
  StockCategorySelectComponent,
  StockClassSelectComponent,
  StockGroupSelectComponent,
  StockLocationSelectComponent,
  StockAccountPresetsSelectComponent,
  InvoiceSelectComponent,
  SINVSelectComponent,
  ARCNSelectComponent,
  ARDNSelectComponent,
  ProjectSelectComponent,
  ProjectTreeSelectComponent,
  ReportPreviewDocComponent,
  BatchEmailComponent,
  BatchPrintComponent,
  DocItemComponent,
  CustomerSelectItemComponent,
  SupplierSelectItemComponent,
  GLPostingModalComponent,
  StkPostingModalComponent,
  DocEditActionsComponent,
  DataEditActionsComponent,
  DocListActionsComponent,
  BatchEmailModalComponent,
  TranxSummaryComponent,
  InventoryTranxSummaryComponent,
  JvTranxSummaryComponent,
  AutoCodeComponent,
  DOAddressSelectComponent,
  CustomerStatusSelectComponent,
  SupplierStatusSelectComponent,
  GenderSelectComponent,
  TaxTypeMYSelectComponent,
  TaxTypePHSelectComponent,
  TaxExemptionRateSelectComponent,
  GovTaxCodeMYSelectComponent,
  GovTaxCodePHSelectComponent,
  QtSelectComponent,
  SoSelectComponent,
  DoSelectComponent,
  CsSelectComponent,
  CsRefundSelectComponent,
  PrSelectComponent,
  PoSelectComponent,
  PdnSelectComponent,
  PcnSelectComponent,
  GrnSelectComponent,
  PinvSelectComponent,
  CpSelectComponent,
  CpRefundSelectComponent,
  SalesDocTransferComponent,
  PurchaseDocTransferComponent,
  BirClassSelectComponent,
  RDOSelectComponent,
  ValidPostingPeriodRuleSelectComponent,
  StockAdjustmentSelectComponent,
  StockOpeningBalanceSelectComponent,
  APWtaxKnockoffComponent,
  ARWtaxKnockoffComponent,
  ARWVatKnockoffComponent,
  APWtaxKnockoffComponent,
  SCNSelectComponent,
  SDNSelectComponent,
  QneTplSelectComponent,
  AutoReconMapItemSelectComponent,
  ARRefundSelectComponent,
  APRefundSelectComponent,
  EInvoiceCountriesSelectComponent,
  EInvoiceUomCodeSelectComponent,
  FurtherDescriptionComponent,
  BankChargesComponent,
  SupDOAddressSelectComponent,
  InvoiceEmailModalComponent,
  EInvoiceValidationControlSelectComponent,
  CustomerInfoModalComponent,
  SupplierInfoModalComponent,
  SSTReturnDeclarationInfoModalComponent,
  EinvoiceConfirmationModalComponent,
  ToolguideModalComponent,
  StockTransferSelectComponent,
  ProjectOBSelectComponent,
  StockInSelectComponent,
  RecurringTransactionModalComponent,
  StockOutSelectComponent,
  FilterListComponent,
  FilterFieldComponent,
  MultiMSICSelectComponent,
  SimpleStockLocationSelectComponent,
  SimpleProjectSelectComponent,
  SimpleMultiSelectComponent,
  GlobalCreditLimitModalComponent,
  PeppolConfirmationModalComponent,
  PriSelectComponent,
  TaxDatePickerComponent,
  TaxCodeMultiSelectComponent,
  StatusMultiSelectComponent,
  AssetTypeSelectComponent,
  StockQuantityControlModalComponent,
  MoreDatePickerComponent,
  FASelectComponent,
  InlineAddPanelComponent,
  DepositComponent,
  BirSupplierItemSelectItemComponent,
  BirSupplierClassSelectComponent,
  ReliefImportationModalComponent
];

const DIRECTIVES: Array<Type<void>> = [];
const PIPES: Array<Type<void>> = [MinusSignToParensPipe];

const SERVICES: Array<Type<void>> = [
  AutoCodeService,
  SalesDocTransferService,
  PurchaseDocTransferService,
  OnlinePaymentService,
  TranscationDetailKeyKownService,
  TransactionDetailColumnAdjustService,
  EinvValidatorsService,
  ReceivePaymentService,
  PurchaserAddConfigService,
  ApCateAddConfigService,
  StockCategoryAddConfigService,
  StockClassAddConfigService,
  StockGroupAddConfigService,
  StockLocationAddConfigService
];
// #endregion

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    PageHowToToolbarButtonComponent,
    InputModule,
    ...STANDALONE_COMPONENTS,
    // third libs
    ...THIRDMODULES,
    CopyTransactionComponent,
    EmailStatusModalComponent
  ],
  providers: [...SERVICES],
  declarations: [
    // your components
    ...COMPONENTS,
    ...DIRECTIVES,
    ...PIPES
  ],
  exports: [
    CommonModule,
    SharedModule,
    PageHowToToolbarButtonComponent,
    InputModule,
    // third libs
    ...THIRDMODULES,
    // your components
    ...STANDALONE_COMPONENTS,
    ...COMPONENTS,
    ...DIRECTIVES
  ]
})
export class SharedFeatureModule {
  constructor(optionalStyles: OptionalStylesService) {
    optionalStyles.loadQuillOnce();
  }
}
